import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import Layout from '../components/Layout';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Trophy, TrendingUp, Target, Users, Award, BarChart3, Calendar } from 'lucide-react';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';

const COLORS = ['#d97706', '#0891b2', '#7c3aed', '#059669', '#dc2626', '#2563eb'];

export default function StatisticsPage({ user }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [availableYears, setAvailableYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState(null);

  useEffect(() => {
    fetchAvailableYears();
  }, []);

  useEffect(() => {
    if (selectedYear) {
      fetchStatistics();
    }
  }, [selectedYear]);

  const fetchAvailableYears = async () => {
    try {
      const response = await axios.get(`${API}/standings/years`);
      setAvailableYears(response.data.years);
      setSelectedYear(response.data.current_year);
    } catch (error) {
      console.error('Failed to fetch years:', error);
    }
  };

  const fetchStatistics = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/standings/statistics?year=${selectedYear}`);
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch statistics:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading || !stats) {
    return (
      <Layout user={user}>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user}>
      <div className="space-y-8" data-testid="statistics-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h1 className="font-barlow font-black text-4xl text-slate-900 uppercase tracking-tight">
              Season Statistics
            </h1>
            <p className="font-inter text-stone-600 mt-1">
              Performance insights and trends for {selectedYear}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Calendar className="w-5 h-5 text-stone-500" />
            <Select value={selectedYear?.toString()} onValueChange={(v) => setSelectedYear(parseInt(v))}>
              <SelectTrigger className="w-32 bg-white border-stone-300 rounded-sm h-10">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {availableYears.map(year => (
                  <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white border border-stone-200 rounded-sm p-6 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="bg-amber-100 p-3 rounded-sm">
                <Trophy className="w-6 h-6 text-amber-600" />
              </div>
              <div>
                <p className="font-inter text-xs text-stone-500 uppercase">Season Champion</p>
                <p className="font-barlow font-bold text-lg text-slate-900">
                  {stats.overall_champion?.name || 'TBD'}
                </p>
                {stats.overall_champion && (
                  <p className="font-mono text-sm text-amber-600">{stats.overall_champion.points} pts</p>
                )}
              </div>
            </div>
          </div>

          <div className="bg-white border border-stone-200 rounded-sm p-6 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="bg-cyan-100 p-3 rounded-sm">
                <BarChart3 className="w-6 h-6 text-cyan-600" />
              </div>
              <div>
                <p className="font-inter text-xs text-stone-500 uppercase">Total Matches</p>
                <p className="font-barlow font-bold text-2xl text-slate-900">{stats.total_matches}</p>
              </div>
            </div>
          </div>

          <div className="bg-white border border-stone-200 rounded-sm p-6 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="bg-violet-100 p-3 rounded-sm">
                <Users className="w-6 h-6 text-violet-600" />
              </div>
              <div>
                <p className="font-inter text-xs text-stone-500 uppercase">Active Players</p>
                <p className="font-barlow font-bold text-2xl text-slate-900">{stats.total_players}</p>
              </div>
            </div>
          </div>

          <div className="bg-white border border-stone-200 rounded-sm p-6 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="bg-emerald-100 p-3 rounded-sm">
                <Target className="w-6 h-6 text-emerald-600" />
              </div>
              <div>
                <p className="font-inter text-xs text-stone-500 uppercase">Divisions</p>
                <p className="font-barlow font-bold text-2xl text-slate-900">{stats.division_distribution.length}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Main Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Monthly Points Trend */}
          <div className="bg-white border border-stone-200 rounded-sm shadow-sm p-6">
            <h2 className="font-barlow font-bold text-xl text-slate-900 uppercase tracking-tight mb-4">
              <TrendingUp className="w-5 h-5 inline mr-2 text-amber-600" />
              Monthly Points Trend
            </h2>
            {stats.monthly_trends.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={stats.monthly_trends}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" />
                  <XAxis 
                    dataKey="month" 
                    tick={{ fontSize: 12 }}
                    tickFormatter={(value) => value.slice(5)}
                  />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#fff', 
                      border: '1px solid #e5e5e5',
                      borderRadius: '4px'
                    }}
                  />
                  <Legend />
                  {stats.top_performers.slice(0, 4).map((player, idx) => (
                    <Line
                      key={player.name}
                      type="monotone"
                      dataKey={player.name}
                      stroke={COLORS[idx % COLORS.length]}
                      strokeWidth={2}
                      dot={{ r: 4 }}
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-64 flex items-center justify-center text-stone-500">
                Not enough data for trends
              </div>
            )}
          </div>

          {/* Division Distribution */}
          <div className="bg-white border border-stone-200 rounded-sm shadow-sm p-6">
            <h2 className="font-barlow font-bold text-xl text-slate-900 uppercase tracking-tight mb-4">
              <Target className="w-5 h-5 inline mr-2 text-cyan-600" />
              Points by Division
            </h2>
            {stats.division_distribution.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={stats.division_distribution}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" />
                  <XAxis dataKey="division" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#fff', 
                      border: '1px solid #e5e5e5',
                      borderRadius: '4px'
                    }}
                  />
                  <Bar dataKey="total_points" fill="#d97706" radius={[4, 4, 0, 0]}>
                    {stats.division_distribution.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-64 flex items-center justify-center text-stone-500">
                No division data available
              </div>
            )}
          </div>
        </div>

        {/* Division Champions & Top Performers */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Division Champions */}
          <div className="bg-white border border-stone-200 rounded-sm shadow-sm overflow-hidden">
            <div className="bg-gradient-to-r from-amber-500 to-amber-600 px-6 py-4">
              <h2 className="font-barlow font-bold text-xl text-white uppercase tracking-tight">
                <Trophy className="w-5 h-5 inline mr-2" />
                Division Champions
              </h2>
            </div>
            <div className="divide-y divide-stone-100">
              {stats.division_champions.length > 0 ? (
                stats.division_champions.map((champ, idx) => (
                  <div key={champ.division} className="p-4 hover:bg-stone-50">
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="inline-block bg-stone-100 text-stone-700 font-barlow font-semibold text-xs px-3 py-1 rounded-sm uppercase mb-2">
                          {champ.division}
                        </span>
                        <div className="flex items-center gap-2">
                          <Trophy className="w-4 h-4 text-amber-500" />
                          <span className="font-inter font-semibold text-slate-900">{champ.champion}</span>
                        </div>
                        {champ.runner_up && (
                          <p className="font-inter text-sm text-stone-500 mt-1">
                            Runner-up: {champ.runner_up} ({champ.runner_up_points} pts)
                          </p>
                        )}
                      </div>
                      <div className="text-right">
                        <span className="font-barlow font-bold text-2xl text-amber-600">{champ.points}</span>
                        <p className="font-inter text-xs text-stone-500">points</p>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="p-8 text-center text-stone-500">No champions yet</div>
              )}
            </div>
          </div>

          {/* Top Performers */}
          <div className="bg-white border border-stone-200 rounded-sm shadow-sm overflow-hidden">
            <div className="bg-gradient-to-r from-slate-700 to-slate-800 px-6 py-4">
              <h2 className="font-barlow font-bold text-xl text-white uppercase tracking-tight">
                <Award className="w-5 h-5 inline mr-2" />
                Top 10 Overall
              </h2>
            </div>
            <div className="divide-y divide-stone-100">
              {stats.top_performers.map((player, idx) => (
                <div key={player.name} className="px-4 py-3 flex items-center justify-between hover:bg-stone-50">
                  <div className="flex items-center gap-3">
                    <span className={`font-barlow font-bold text-lg w-8 text-center ${
                      idx === 0 ? 'text-amber-500' : idx === 1 ? 'text-stone-400' : idx === 2 ? 'text-amber-700' : 'text-stone-500'
                    }`}>
                      #{idx + 1}
                    </span>
                    <span className="font-inter text-slate-900">{player.name}</span>
                  </div>
                  <span className="font-mono font-bold text-slate-900">{player.points} pts</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Most Improved & Most Consistent */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Most Improved */}
          <div className="bg-white border border-stone-200 rounded-sm shadow-sm overflow-hidden">
            <div className="bg-gradient-to-r from-emerald-500 to-emerald-600 px-6 py-4">
              <h2 className="font-barlow font-bold text-xl text-white uppercase tracking-tight">
                <TrendingUp className="w-5 h-5 inline mr-2" />
                Most Improved
              </h2>
            </div>
            <div className="divide-y divide-stone-100">
              {stats.most_improved.length > 0 ? (
                stats.most_improved.map((player, idx) => (
                  <div key={player.name} className="p-4 hover:bg-stone-50">
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="font-inter font-semibold text-slate-900">{player.name}</span>
                        <div className="flex items-center gap-4 mt-1">
                          <span className="font-inter text-sm text-stone-500">
                            1st half avg: <span className="font-mono">{player.first_half_avg}</span>
                          </span>
                          <span className="text-stone-300">→</span>
                          <span className="font-inter text-sm text-stone-500">
                            2nd half avg: <span className="font-mono">{player.second_half_avg}</span>
                          </span>
                        </div>
                      </div>
                      <div className={`font-barlow font-bold text-lg ${player.improvement > 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                        {player.improvement > 0 ? '+' : ''}{player.improvement}
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="p-8 text-center text-stone-500">Not enough data</div>
              )}
            </div>
          </div>

          {/* Most Consistent */}
          <div className="bg-white border border-stone-200 rounded-sm shadow-sm overflow-hidden">
            <div className="bg-gradient-to-r from-violet-500 to-violet-600 px-6 py-4">
              <h2 className="font-barlow font-bold text-xl text-white uppercase tracking-tight">
                <Target className="w-5 h-5 inline mr-2" />
                Most Consistent
              </h2>
            </div>
            <div className="divide-y divide-stone-100">
              {stats.most_consistent.length > 0 ? (
                stats.most_consistent.map((player, idx) => (
                  <div key={player.name} className="p-4 hover:bg-stone-50">
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="font-inter font-semibold text-slate-900">{player.name}</span>
                        <div className="flex items-center gap-4 mt-1">
                          <span className="font-inter text-sm text-stone-500">
                            {player.matches} matches
                          </span>
                          <span className="font-inter text-sm text-stone-500">
                            Avg: <span className="font-mono">{player.avg_points}</span> pts
                          </span>
                        </div>
                      </div>
                      <div className="text-right">
                        <span className="font-barlow font-bold text-lg text-violet-600">
                          {player.consistency_score}%
                        </span>
                        <p className="font-inter text-xs text-stone-500">consistency</p>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="p-8 text-center text-stone-500">Not enough data</div>
              )}
            </div>
          </div>
        </div>

        {/* Division Stats Table */}
        <div className="bg-white border border-stone-200 rounded-sm shadow-sm overflow-hidden">
          <div className="bg-stone-100 px-6 py-4 border-b border-stone-200">
            <h2 className="font-barlow font-bold text-xl text-slate-900 uppercase tracking-tight">
              Division Statistics
            </h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-stone-50 border-b border-stone-200">
                <tr>
                  <th className="text-left py-3 px-4 font-barlow font-bold text-xs uppercase tracking-wider text-stone-600">Division</th>
                  <th className="text-center py-3 px-4 font-barlow font-bold text-xs uppercase tracking-wider text-stone-600">Players</th>
                  <th className="text-center py-3 px-4 font-barlow font-bold text-xs uppercase tracking-wider text-stone-600">Total Points</th>
                  <th className="text-center py-3 px-4 font-barlow font-bold text-xs uppercase tracking-wider text-stone-600">Avg Points</th>
                  <th className="text-center py-3 px-4 font-barlow font-bold text-xs uppercase tracking-wider text-stone-600">High</th>
                  <th className="text-center py-3 px-4 font-barlow font-bold text-xs uppercase tracking-wider text-stone-600">Low</th>
                </tr>
              </thead>
              <tbody>
                {stats.division_distribution.map((div, idx) => (
                  <tr key={div.division} className="border-b border-stone-100 hover:bg-stone-50">
                    <td className="py-4 px-4">
                      <span className="inline-block bg-amber-100 text-amber-800 font-barlow font-semibold text-xs px-3 py-1 rounded-sm uppercase">
                        {div.division}
                      </span>
                    </td>
                    <td className="py-4 px-4 text-center font-mono text-slate-900">{div.players}</td>
                    <td className="py-4 px-4 text-center font-mono font-bold text-slate-900">{div.total_points}</td>
                    <td className="py-4 px-4 text-center font-mono text-slate-700">{div.avg_points}</td>
                    <td className="py-4 px-4 text-center font-mono text-emerald-600">{div.max_points}</td>
                    <td className="py-4 px-4 text-center font-mono text-stone-500">{div.min_points}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </Layout>
  );
}
